import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, ValidatorFn} from '@angular/forms';
import {NgbActiveOffcanvas, NgbDateStruct, NgbInputDatepicker, NgbOffcanvas} from '@ng-bootstrap/ng-bootstrap';
import {map, tap} from 'rxjs';
import {UserActivityParams} from '@app/_models/api';
import {ApiService} from '@app/_services/api.service';
import {HistoryDrawerComponent} from '@app/_components/history-drawer/history-drawer.component';
import {toActivityEvents} from '@app/_components/history-drawer/history-drawer.helpers';
import {HistoryFeed} from '@app/_components/history-drawer/history-feed';
import {toDateStruct, toIsoDate} from '@app/_helpers/date-struct';
import {isControlInvalid} from '@app/_helpers/form-validation';

export type ActivityUser = {
    cognitoUserId: string;
    fullName: string;
};

const fromOnOrBeforeTo: ValidatorFn = group => {
    const from = toIsoDate(group.get('from')?.value ?? null);
    const to = toIsoDate(group.get('to')?.value ?? null);
    return from && to && from > to ? {rangeOrder: true} : null;
};

export function openUserActivityDrawer(offcanvasService: NgbOffcanvas, user: ActivityUser) {
    const drawerRef = offcanvasService.open(UserActivityDrawerComponent, {
        position: 'end',
        panelClass: 'history-offcanvas',
        ariaLabelledBy: 'history-drawer-title'
    });
    (drawerRef.componentInstance as UserActivityDrawerComponent).show(user);
}

@Component({
    selector: 'app-user-activity-drawer',
    imports: [HistoryDrawerComponent, ReactiveFormsModule, NgbInputDatepicker],
    templateUrl: './user-activity-drawer.component.html',
    // The history drawer scrolls inside its parent's height, so this wrapper must fill the offcanvas panel.
    styles: ':host { display: block; height: 100%; }',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserActivityDrawerComponent {
    private apiService = inject(ApiService);
    private activeOffcanvas = inject(NgbActiveOffcanvas);

    readonly user = signal<ActivityUser | null>(null);
    readonly rangeError = signal<string | null>(null);
    readonly exportName = computed(() =>
        `activity-${(this.user()?.fullName ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`);
    readonly rangeForm = new FormGroup({
        from: new FormControl<NgbDateStruct | null>(null),
        to: new FormControl<NgbDateStruct | null>(null),
    }, {validators: fromOnOrBeforeTo});
    protected readonly isControlInvalid = isControlInvalid;

    // Load more and Try again search the applied range, not dates typed since.
    private range: Pick<UserActivityParams, 'from' | 'to'> = {from: '', to: ''};

    readonly feed = new HistoryFeed({
        fetchPage: page => {
            const user = this.user()!;
            this.rangeError.set(null);
            return this.apiService.getUserActivity(user.cognitoUserId, {...this.range, page}).pipe(
                tap(response => {
                    if (page === 1) {
                        this.range = {from: response.from, to: response.to};
                        this.rangeForm.setValue({from: toDateStruct(response.from), to: toDateStruct(response.to)});
                    }
                }),
                map(response => ({
                    events: toActivityEvents(response.saves, user.fullName),
                    totalCount: response.totalCount,
                    pageSize: response.pageSize
                }))
            );
        },
        handleError: error => {
            if (error.status !== 400) {
                return false;
            }

            this.rangeError.set(error.error?.detail ?? 'The date range is not valid.');
            return true;
        }
    });

    constructor() {
        inject(DestroyRef).onDestroy(() => this.feed.cancel());
    }

    show(user: ActivityUser) {
        this.user.set(user);
        this.feed.load(1);
    }

    onApplyRange() {
        if (this.rangeForm.invalid) {
            this.rangeForm.markAllAsTouched();
            return;
        }

        const range = this.rangeForm.value;
        this.range = {from: toIsoDate(range.from ?? null), to: toIsoDate(range.to ?? null)};
        this.feed.load(1);
    }

    close() {
        this.activeOffcanvas.dismiss();
    }
}
